import React, { useCallback, useEffect, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import styles from './Modal.module.scss';
import { useNavigate } from 'react-router-dom';
import Portal from '../Portal/Portal';
import Button from 'component/atom/forms/button/Button';
import { useBrandingContext } from 'app-context/BrandingsContext';
import SvgWithBranding from '../branding/withBrandingColor.hoc';

const Modal = ({
  isOpen,
  onClose,
  title,
  modalContent,
  onSubmit,
  route,
  onSubmitstyles,
  isLoading,
  classname,
  children,
  isHtml,
  showCancel = true,
  exitComponent,
  footerComponent,
  contentClassName,
  cancelClassName,
  cancelVariant = 'dark',
  disabledFooter,
  disabledCancel,
  showSubmit = true,
  isWithHeader = true,
}) => {
  const navigate = useNavigate();
  const nodeRef = useRef(null);
  const shouldCloseRef = useRef(false);

  const { brandingData } = useBrandingContext();
  const { page_TextColor } = brandingData;

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'auto';
  }, [isOpen]);

  const handleClose = useCallback(() => {
    if (onClose) onClose();
  }, [onClose]);

  const handleSubmit = () => {
    if (onSubmit) onSubmit();
    if (route) {
      document.body.style.overflow = 'auto';
      navigate(route);
    }
  };

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') handleClose();
    };

    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, handleClose]);

  return (
    <Portal>
      <CSSTransition
        in={isOpen}
        timeout={150}
        classNames={{
          enter: styles['fade-enter'],
          enterActive: styles['fade-enter-active'],
          exit: styles['fade-exit'],
          exitActive: styles['fade-exit-active'],
        }}
        unmountOnExit
        nodeRef={nodeRef}
      >
        <div
          ref={nodeRef}
          className={styles['modal-overlay']}
          style={{ color: page_TextColor }}
          onPointerDown={(e) => {
            shouldCloseRef.current = e.target === e.currentTarget;
          }}
          onClick={(e) => {
            if (disabledCancel) return;
            if (!shouldCloseRef.current) return;
            if (e.target !== e.currentTarget) return;
            handleClose();
          }}
        >
          <div
            className={`${styles['modal-content']} ${classname || ''}`}
            onPointerDown={() => {
              shouldCloseRef.current = false;
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {isWithHeader ? (
              <>
                <div className={styles['title-container']}>
                  <p className={styles['page-title']}>{title}</p>
                  <div className={styles['exit-container']}>
                    {exitComponent ? exitComponent : null}
                    <SvgWithBranding
                      type='exit_modal'
                      onClick={() => {
                        if (!disabledCancel) handleClose();
                      }}
                      className={styles['exit-img']}
                    />
                  </div>
                </div>
                <div className={styles.horizantalLine} />
              </>
            ) : null}

            {modalContent?.subtitle && (
              <div className={styles['sub-title']}>
                {modalContent?.subtitle ? (
                  <p>{modalContent?.subtitle}</p>
                ) : null}
              </div>
            )}

            <div className={`${styles.content} ${contentClassName || ''}`}>
              {modalContent?.component}
              {isHtml ? (
                <div dangerouslySetInnerHTML={{ __html: children }} />
              ) : (
                children
              )}
            </div>

            {modalContent?.component ? (
              <div className={styles.footer}>
                <div>{footerComponent ?? null}</div>
                <div className={styles['actions']}>
                  {showCancel && (
                    <Button
                      onClick={handleClose}
                      label='Cancel'
                      variant={cancelVariant}
                      className={cancelClassName}
                      disabled={disabledCancel}
                    />
                  )}
                  {showSubmit && (
                    <Button
                      onClick={handleSubmit}
                      label={onSubmitstyles?.onSubmit?.label || 'Submit'}
                      type={onSubmitstyles?.onSubmit?.type}
                      isLoading={isLoading}
                      variant={onSubmitstyles?.onSubmit?.variant}
                      opicBackground={onSubmitstyles?.onSubmit?.opicBackground}
                      disabled={disabledFooter || isLoading}
                    />
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </CSSTransition>
    </Portal>
  );
};

export default Modal;
